import { useMemo } from "react";
import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";
import styles from "./Dropdown.module.css";

const CustomDropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown
        size={14}
        strokeWidth={2.2}
        style={{
          transition: "transform 0.2s ease",
          transform: props.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
        }}
      />
    </components.DropdownIndicator>
  );
};

const defaultSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "10px",
    borderColor: state.isFocused ? "var(--color-dark-bg, #27306f)" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(39, 48, 111, 0.15)" : "none",
    "&:hover": {
      borderColor: "var(--color-dark-bg, #27306f)",
    },
    fontSize: "13px",
    fontFamily: "'Poppins', 'Montserrat', -apple-system, sans-serif",
    cursor: "pointer",
    transition: "all 0.15s ease-in-out",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: "500",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "13px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "var(--color-dark-bg, #27306f)" : "#64748b",
    padding: "4px 8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: "var(--color-dark-bg, #27306f)",
    },
    transition: "color 0.15s ease",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 99999,
    borderRadius: "10px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e2e8f0",
    padding: "4px",
    backgroundColor: "#ffffff",
    minWidth: "220px",
    width: "max-content",
    maxWidth: "380px",
  }),
  menuList: (base) => ({
    ...base,
    padding: "4px",
    maxHeight: "260px",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: "6px",
    marginBottom: "4px",
    "&:last-child": {
      marginBottom: "0",
    },
    padding: "8px 12px",
    fontSize: "13px",
    fontFamily: "'Poppins', 'Montserrat', -apple-system, sans-serif",
    fontWeight: state.isSelected ? "600" : "400",
    backgroundColor: state.isSelected
      ? "var(--color-dark-bg, #27306f)"
      : state.isFocused
        ? "#f1f5f9"
        : "transparent",
    color: state.isSelected ? "#ffffff" : "#1e293b",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "background 0.12s ease",
    "&:active": {
      backgroundColor: "var(--color-dark-bg, #27306f)",
      color: "#ffffff",
    },
  }),
};

const normalizeOptions = (opts = []) => {
  if (!Array.isArray(opts)) return [];
  return opts.map((opt) => {
    if (opt === null || opt === undefined) {
      return { label: "", value: "" };
    }
    if (typeof opt === "object") {
      const label = opt.label ?? opt.name ?? opt.title ?? String(opt.value ?? "");
      const val = opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : opt);
      return { ...opt, label: String(label), value: val };
    }
    return { label: String(opt), value: opt };
  });
};

const getSelectedOption = (normalizedOptions, value, isMulti) => {
  if (isMulti) {
    if (!value) return [];
    const valuesArray = Array.isArray(value) ? value : [value];
    return valuesArray
      .map((v) => {
        if (v && typeof v === "object" && "value" in v) return v;
        const match = normalizedOptions.find((opt) => String(opt.value) === String(v));
        return match || (v !== "" && v !== null && v !== undefined ? { label: String(v), value: v } : null);
      })
      .filter(Boolean);
  }

  if (value === null || value === undefined) return null;
  if (typeof value === "object" && "value" in value) return value;

  const match = normalizedOptions.find((opt) => String(opt.value) === String(value));
  if (match) return match;

  if (value === "") {
    return normalizedOptions.find((opt) => opt.value === "") || null;
  }

  return { label: String(value), value };
};

const Dropdown = ({
  value,
  onChange,
  options = [],
  disabled = false,
  isDisabled = false,
  className = "",
  classNamePrefix = "react-select",
  placeholder = "Select...",
  isMulti = false,
  isClearable = false,
  isSearchable = true,
  name,
  menuPlacement = "auto",
  styles: customStyles,
  ...rest
}) => {
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);

  const selectedValue = useMemo(
    () => getSelectedOption(normalizedOptions, value, isMulti),
    [normalizedOptions, value, isMulti]
  );

  const handleSelectChange = (selected, actionMeta) => {
    if (!onChange) return;
    if (isMulti) {
      const values = selected ? selected.map((opt) => opt.value) : [];
      onChange(values, selected, actionMeta);
    } else {
      const val = selected ? selected.value : "";
      onChange(val, selected, actionMeta);
    }
  };

  const mergedStyles = useMemo(() => {
    if (!customStyles) return defaultSelectStyles;
    return {
      ...defaultSelectStyles,
      ...customStyles,
      menuPortal: (base, state) => ({
        ...defaultSelectStyles.menuPortal(base, state),
        ...(customStyles.menuPortal ? customStyles.menuPortal(base, state) : {}),
      }),
      menu: (base, state) => ({
        ...defaultSelectStyles.menu(base, state),
        ...(customStyles.menu ? customStyles.menu(base, state) : {}),
      }),
    };
  }, [customStyles]);

  const disabledState = disabled || isDisabled;

  return (
    <div className={[styles.container, className].filter(Boolean).join(" ")}>
      <Select
        value={selectedValue}
        onChange={handleSelectChange}
        options={normalizedOptions}
        isDisabled={disabledState}
        isMulti={isMulti}
        isClearable={isClearable}
        isSearchable={isSearchable}
        placeholder={placeholder}
        menuPlacement={menuPlacement}
        menuPosition="fixed"
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={mergedStyles}
        components={{ DropdownIndicator: CustomDropdownIndicator, ...(rest.components || {}) }}
        classNamePrefix={classNamePrefix}
        name={name}
        {...rest}
      />
      {name && !isMulti && (
        <input
          type="hidden"
          name={name}
          value={selectedValue?.value ?? value ?? ""}
        />
      )}
    </div>
  );
};

export default Dropdown;
